import { FC, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { connect } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import clsx from "clsx";
import useWindowBreakpoints from "../../../hooks/useWindowBreakpoints";
import DesktopToolbar from "../DesktopToolbar";
import ReportHeader from "../ReportHeader";
import Filters from "../Filters";
import FloatingMenu from "../FloatingMenu";
import BarcodeScanner from "../Scanner";
import Annotate from "../Annotate";
import Table from "@albertsons/uds/molecule/Table";
import {
  ButtonGroup,
  TabHeader,
  Table as CommonTable,
} from "../../../components";
import {
  fetchUnitsData,
  initDashboardState,
  setSelectedUnitTab as setSelectedUnitTabAction,
} from "../../../store/modules/dashboard";
import { fetchRetailAdjustmentsData } from "../../../store/modules/retailAdjustments";
import {
  monitorViewReport,
  monitorBarcodeScanUsage,
} from "../../../store/modules/monitor";
import {
  getDesktopTableColumns,
  getMobileTableColumns,
  getReportRows,
  UnitInfoModal,
} from "../../../utils/report-columns";
import { unitTabs } from "../../../constants/units";
import {
  blockBodyScroll,
  releaseBodyScroll,
} from "../../../utils/blockScrolling";
import { getBrowser, isMobile } from "../../../utils/isMobile";
import { fetchDcResearchData } from "../../../store/modules/dcResearch";
import {
  resetOrderHistory,
  fetchDcStoreData,
  fetchStoreOrderData,
  fetchBuyerCommentsData,
  fetchDcStoreTotal,
  fetchStoreOrderTotal,
} from "../../../store/modules/orderHistory";
import {
  fetchOrderHistoryDsdData,
  fetchOrderHistoryDsdTotalQuantity,
} from "../../../store/modules/dsdOrderHistory";
import Cards from "../Cards";
import WarehouseOrderHistory from "../WarehouseOrderHistory";
import DSDOrderHistory from "../DSDOrderHistory";
import { getItemType, CARDS, REPORT_NAME } from "./utils";
import { TableSchema } from "../../../pages/about/utils";
import { canMonitor } from "../../../utils/app-env";
import { defaultItemType } from "../../../constants/filters";
import { BarcodeValue } from "../../../types/barcode.types";
import { DCResearchCards } from "../../../types/cards.types";
import {
  ItemType,
  IUnitsData,
  IUnitsTotalData,
  UnitTab,
} from "../../../types/data.types";
import { FilterType, IFilterOptions } from "../../../types/filters.types";
import { IRetailAdjustment } from "../../../types/retailAdjustments.types";
import {
  IMonitorViewReport,
  IBarcodeUsage,
} from "../../../types/monitor.types";
import { HISTORY_TAB } from "../../../types/order.types";
import { TableColumn } from "../../Table/CommonTable.types";
import "./styles.css";

interface ReportLayoutProps {
  forceScreen?: "extraSmall" | "mobile" | "desktop";
  canScanBarcode?: boolean;
  isInitialized: boolean;
  isDetectedLocation: boolean;
  kpiDefinition: TableSchema[];
  reportConsiderations: string[];
  productDescription: string;
  isUnitsDataLoading: number;
  refreshDataKey: number;
  isTrackedViewReport: boolean;
  sellingUnitsData: IUnitsData;
  shippingUnitsData: IUnitsData;
  vendorUnitsData: IUnitsData;
  totalWarehouseData: IUnitsTotalData;
  totalStoreData: IUnitsTotalData;
  selectedFilterOptions: IFilterOptions;
  itemType: ItemType;
  selectedUnitTab: UnitTab;
  selectedHistoryTab: HISTORY_TAB;
  dcResearchData: DCResearchCards | null;
  retailAdjustmentsData: IRetailAdjustment[];
  isDcResearchCardsLoading: boolean;
  isLoadingRetailAdjustmentsData: boolean;
  isLoadingDcStoreData: boolean;
  isLoadingStoreOrderData: boolean;
  isLoadingBuyerCommentsData: boolean;
  isLoadingOrderHistoryData: boolean;
  isMainView?: boolean;
  isRetailOps?: boolean;
  loadFilters?: FilterType[];
  initDashboardState: () => void;
  fetchUnitsData: (filters: IFilterOptions, isRetailOps?: boolean) => void;
  setSelectedUnitTab: (value: UnitTab) => void;
  resetOrderHistory: () => void;
  fetchDcStoreData: (filters: IFilterOptions) => void;
  fetchStoreOrderData: (filters: IFilterOptions) => void;
  fetchBuyerCommentsData: (filters: IFilterOptions) => void;
  fetchDcStoreTotal: (filters: IFilterOptions) => void;
  fetchStoreOrderTotal: (filters: IFilterOptions) => void;
  fetchOrderHistoryDsdData: (filters: IFilterOptions) => void;
  fetchOrderHistoryDsdTotalQuantity: (filters: IFilterOptions) => void;
  fetchDCResearchCards: (filters: IFilterOptions, fields: string[]) => void;
  fetchRetailAdjustmentsData: (filters: IFilterOptions) => void;
  monitorViewReport: (data: IMonitorViewReport) => void;
  monitorBarcodeScanUsage: (data: IBarcodeUsage) => void;
}

const researchTabs: { id: string; label: string; value: 0 | 1 }[] = [
  { id: "dc-research", label: "DC Research", value: 0 },
  { id: "retail-inv-adj", label: "Retail Inv Adj", value: 1 },
];

const currrentTime = performance.now();

type RetailAdjustmentTableRow = IRetailAdjustment & {
  id: string;
};

const retailAdjustmentsColumns: TableColumn<RetailAdjustmentTableRow>[] = [
  {
    id: "adjustmentType",
    label: "ADJUSTMENT TYPE",
    value: (row: RetailAdjustmentTableRow) => (
      <div className="px-3 py-2 truncate" title={row.adjustmentType || "NA"}>
        {row.adjustmentType || "NA"}
      </div>
    ),
    align: "center",
  },
  {
    id: "qty",
    label: "QTY",
    type: "number",
    value: (row: RetailAdjustmentTableRow) => (
      <div className="px-3 py-2 text-center">{row.qty ?? 0}</div>
    ),
    align: "center",
  },
];

const ReportLayout: FC<ReportLayoutProps> = ({
  forceScreen,
  canScanBarcode = true,
  isInitialized,
  isDetectedLocation,
  kpiDefinition,
  reportConsiderations,
  productDescription,
  isUnitsDataLoading,
  refreshDataKey,
  isTrackedViewReport,
  totalWarehouseData,
  totalStoreData,
  selectedFilterOptions,
  itemType,
  sellingUnitsData,
  shippingUnitsData,
  vendorUnitsData,
  selectedUnitTab,
  selectedHistoryTab,
  dcResearchData,
  retailAdjustmentsData,
  isDcResearchCardsLoading,
  isLoadingRetailAdjustmentsData,
  isLoadingDcStoreData,
  isLoadingStoreOrderData,
  isLoadingBuyerCommentsData,
  isLoadingOrderHistoryData,
  isMainView = false,
  isRetailOps = false,
  loadFilters,
  initDashboardState,
  fetchUnitsData,
  setSelectedUnitTab,
  resetOrderHistory,
  fetchDcStoreData,
  fetchStoreOrderData,
  fetchBuyerCommentsData,
  fetchDcStoreTotal,
  fetchStoreOrderTotal,
  fetchOrderHistoryDsdData,
  fetchOrderHistoryDsdTotalQuantity,
  fetchDCResearchCards,
  fetchRetailAdjustmentsData,
  monitorViewReport,
  monitorBarcodeScanUsage,
}) => {
  const [showAnnotateModal, setShowAnnotateModal] = useState(false);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [barcodeValue, setBarcodeValue] = useState<BarcodeValue | null>(null);
  const [isOpenUnitInfoModal, setIsOpenUnitInfoModal] = useState(false);
  const [selectedResearchTab, setSelectedResearchTab] = useState<0 | 1>(0);

  const prevFiltersRef = useRef("");
  const prevItemTypeRef = useRef(JSON.stringify(defaultItemType));
  const mainScreenContentRef = useRef<HTMLDivElement>(null);

  const { isFar, isNonFar, isDsd, isUnified } = getItemType(itemType);

  const { getScreen } = useWindowBreakpoints();
  const screen = getScreen(forceScreen);

  const { accounts } = useMsal();
  const isActualMobile = isMobile();
  const browser = getBrowser();

  const isLoading =
    !isInitialized ||
    isUnitsDataLoading > 0 ||
    isDcResearchCardsLoading ||
    isLoadingRetailAdjustmentsData ||
    isLoadingDcStoreData ||
    isLoadingStoreOrderData ||
    isLoadingBuyerCommentsData ||
    isLoadingOrderHistoryData;

  const unitsData = useMemo(() => {
    switch (selectedUnitTab) {
      case 0:
        return sellingUnitsData;
      case 1:
        return shippingUnitsData;
      case 2:
        return vendorUnitsData;
    }
  }, [selectedUnitTab, sellingUnitsData, shippingUnitsData, vendorUnitsData]);

  const isStoreFilterApplied = useMemo(
    () => selectedFilterOptions.retailStore.length > 0,
    [selectedFilterOptions],
  );

  const showResearchSection = isUnified || !isDsd;

  const retailAdjustmentsItems = useMemo(
    () =>
      retailAdjustmentsData.map((adjustment, index) => ({
        ...adjustment,
        id: `${adjustment.adjustmentType || "na"}-${index}`,
      })),
    [retailAdjustmentsData],
  );

  const showCards = showResearchSection && (
    <>
      <div className={clsx("pt-2", screen.small && "px-2")}>
        <ButtonGroup
          forceScreen={forceScreen}
          buttons={researchTabs}
          value={selectedResearchTab}
          setValue={setSelectedResearchTab}
        />
      </div>
      {selectedResearchTab === 0 ? (
        <Cards<DCResearchCards>
          forceScreen={forceScreen}
          sectionLabel="DC Research"
          cards={CARDS}
          isLoading={isDcResearchCardsLoading}
          researchData={dcResearchData}
          showCollapsedView={isMainView && screen.small}
          selectedUnitTab={selectedUnitTab}
        />
      ) : (
        <div className="mt-3">
          <TabHeader title="Retail Inv Adj" />
          <div className="pt-5 pb-2">
            <div className="border border-gray-300">
              <CommonTable<RetailAdjustmentTableRow>
                id="retail-adjustments-table"
                isLoading={isLoadingRetailAdjustmentsData}
                rows={retailAdjustmentsItems}
                columns={retailAdjustmentsColumns}
                className="retail-adjustments-table"
              />
            </div>
            <p className="text-sm pt-2">
              <span className="text-red-500">*</span>
              Adjustment value reflected is for today&apos;s date only and is in
              selling units.
            </p>
          </div>
        </div>
      )}
    </>
  );

  let showOrderHistory: React.ReactNode = null;
  if (isMainView) {
    if (!isUnified && screen.large) {
      showOrderHistory = (
        <>
          {(isFar || isNonFar) && <WarehouseOrderHistory />}
          {isDsd && <DSDOrderHistory />}
        </>
      );
    }
  } else if (isDsd) {
    showOrderHistory = <DSDOrderHistory />;
  } else {
    showOrderHistory = <WarehouseOrderHistory />;
  }

  const openAnnotateModal = () => {
    setShowAnnotateModal(true);
    blockBodyScroll();
  };

  const closeAnnotateModal = () => {
    setShowAnnotateModal(false);
    releaseBodyScroll();
  };

  const resetBarcodeValue = () => {
    setBarcodeValue(null);
  };

  const openBarcodeModal = () => {
    setShowBarcodeModal(true);
    blockBodyScroll();
  };

  const closeBarcodeModal = () => {
    setShowBarcodeModal(false);
    releaseBodyScroll();
  };

  const updateBarcodeValue = (barcode: BarcodeValue) => {
    if (canMonitor) {
      const { name, username } = accounts[0];
      monitorBarcodeScanUsage({
        name: name ?? "",
        email: username,
        type: barcode.type,
        scannedvalue: barcode.data,
        deviceid: localStorage.getItem("scandit-device-id") ?? "",
        monitorid: sessionStorage.getItem("monitorId") ?? "",
      });
    }
    setBarcodeValue(barcode);
    closeBarcodeModal();
  };

  const fetchOrderHistoryData = () => {
    if (!isDsd) {
      resetOrderHistory();
      switch (selectedHistoryTab) {
        case HISTORY_TAB.DC_ORDER:
          fetchDcStoreData(selectedFilterOptions);
          fetchDcStoreTotal(selectedFilterOptions);
          break;
        case HISTORY_TAB.STORE_ORDER:
          fetchStoreOrderData(selectedFilterOptions);
          fetchStoreOrderTotal(selectedFilterOptions);
          break;
        case HISTORY_TAB.BUYER_COMMENTS:
          fetchBuyerCommentsData(selectedFilterOptions);
          break;
      }
    }
    if (isDsd) {
      fetchOrderHistoryDsdData(selectedFilterOptions);
      fetchOrderHistoryDsdTotalQuantity(selectedFilterOptions);
    }
  };

  const fetchItemData = () => {
    fetchOrderHistoryData();
    if (showResearchSection) {
      fetchRetailAdjustmentsData(selectedFilterOptions);
    }
    if (!isUnified && (isFar || isNonFar)) {
      fetchDCResearchCards(selectedFilterOptions, [
        "dcEstimatedArrivalDate",
        `dcLastDateReceived
        cosmoLimits`,
        `thisWeeksDemandSellingUnits
        thisWeeksDemandShippingUnits
        thisWeeksDemandVendorUnits
        codeDatePickSlot`,
        "mgrOptionR",
      ]);
    }
  };

  useEffect(() => {
    if (isInitialized && isDetectedLocation) {
      const newFilters = JSON.stringify(selectedFilterOptions);
      if (newFilters !== prevFiltersRef.current) {
        fetchUnitsData(selectedFilterOptions, isRetailOps);
        fetchItemData();
        prevFiltersRef.current = newFilters;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedFilterOptions,
    selectedUnitTab,
    isInitialized,
    isDetectedLocation,
  ]);

  useEffect(() => {
    if (isInitialized) {
      const newItemType = JSON.stringify(itemType);
      if (newItemType !== prevItemTypeRef.current) {
        fetchItemData();
        prevItemTypeRef.current = newItemType;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, itemType]);

  useEffect(() => {
    if (!canMonitor || isTrackedViewReport || isLoading) return;
    const { name, username } = accounts[0];
    const loadingTime = performance.now() - currrentTime;
    monitorViewReport({
      name: name ?? "",
      email: username,
      loading_time: Math.round(loadingTime / 1000),
      platform: isActualMobile ? "Mobile" : "Desktop",
      browser,
      report: "IMPACT mView Dashboard",
      page: REPORT_NAME,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrackedViewReport, isLoading]);

  useEffect(() => {
    if (refreshDataKey) {
      fetchUnitsData(selectedFilterOptions, isRetailOps);
      fetchItemData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshDataKey]);

  useEffect(() => {
    initDashboardState();
    document.title = `${REPORT_NAME} | IMPACT mVIEW Dashboard`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTableRows = useCallback((): any => {
    return getReportRows({
      isSmall: screen.small,
      isDsd,
      isFar,
      isRetailOps,
      isUnified,
      unitsData,
      isStoreFilterApplied,
      totalWarehouseData,
      totalStoreData,
      selectedUnitTab,
      isUnitsDataLoading,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isUnitsDataLoading,
    unitsData,
    isStoreFilterApplied,
    totalWarehouseData,
    totalStoreData,
    selectedUnitTab,
    isUnified,
    isFar,
    isDsd,
    screen,
  ]);

  const getTableColumns = useCallback(() => {
    if (screen.small)
      return getMobileTableColumns(
        unitTabs[selectedUnitTab].label,
        screen.extraSmall,
        isUnitsDataLoading,
        isRetailOps,
        () => setIsOpenUnitInfoModal(true),
      );
    return getDesktopTableColumns(
      isUnified,
      isDsd,
      kpiDefinition,
      reportConsiderations,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnitsDataLoading, screen, selectedUnitTab, isUnified, isDsd]);

  return (
    <div className="relative">
      <div className="row-col-reverse" />
      <div ref={mainScreenContentRef}>
        {screen.large && <DesktopToolbar isLoading={isLoading} />}
        <div
          className={clsx(screen.small && !showAnnotateModal && "mb-[80px]")}
        >
          <ReportHeader
            forceScreen={forceScreen}
            showMviewLabel
            showDateTimeFetched
            showAboutLink
          />
          <div className={clsx("flex", screen.small && "flex-col")}>
            <Filters
              showWarehouseDivision={isUnified || !isDsd}
              showWarehouse={isUnified || !isDsd}
              forceScreen={forceScreen}
              barcodeValue={barcodeValue}
              loadFilters={loadFilters}
              resetBarcodeValue={resetBarcodeValue}
            />
            {screen.small && (
              <>
                <div className="col-span-2 p-1 font-semibold">
                  <p className="text-[#007fb6]">Cross Network Inventory</p>
                </div>
                <div className="font-['Arial'] pt-2 pb-4 px-2">
                  <span className="font-semibold mr-1">CIC/Desc:</span>
                  {productDescription}
                </div>
              </>
            )}
            <div className={clsx(screen.large ? "px-4" : "px-2 pb-6")}>
              {screen.large ? (
                <div className="flex justify-end pr-6">
                  <ButtonGroup
                    title="Unit Toggle:"
                    buttons={unitTabs}
                    value={selectedUnitTab}
                    setValue={setSelectedUnitTab}
                  />
                </div>
              ) : (
                <ButtonGroup
                  title="Unit Toggle:"
                  buttons={unitTabs}
                  value={selectedUnitTab}
                  setValue={setSelectedUnitTab}
                />
              )}
              {screen.large && <TabHeader title="Cross Network Inventory" />}
              <Table
                id="units-table"
                itemKey="id"
                items={getTableRows()}
                columns={getTableColumns()}
                noHeader
                noPagination
                dividers
                className={clsx(
                  "units-table",
                  screen.small ? "small-table" : "large-table",
                )}
              />
              {!isUnified && isNonFar ? (
                <>
                  <p className="text-sm pt-2">
                    <span className="text-red-500">*</span>
                    No Store Inventory data available for non-FaR items at this
                    time
                  </p>
                  {!isRetailOps ? (
                    <p className="text-sm pt-2">
                      <span className="text-red-500">*</span>
                      ITS is available only for items that have DA forecast
                    </p>
                  ) : null}
                </>
              ) : null}
              {!isRetailOps && (isUnified || isFar || isDsd) ? (
                <p className="text-sm pt-2">
                  <span className="text-red-500">*</span>
                  ITS is available only for items that have FaR forecast
                </p>
              ) : null}
              {!isUnified && isDsd ? (
                <p className="text-sm pt-2">
                  <span className="text-red-500">*</span>
                  No In-Transit to Store data for DSD at this time
                </p>
              ) : null}
              {showCards}
              {showOrderHistory}
            </div>
          </div>
        </div>
        {screen.small && (
          <FloatingMenu
            forceScreen={forceScreen}
            canScanBarcode={canScanBarcode}
            isAnnotating={showAnnotateModal}
            showPagesButton={isMainView}
            showAnnotateButton={isMainView}
            showExpandButton={isMainView}
            openAnnotateModal={openAnnotateModal}
            openBarcodeModal={openBarcodeModal}
          />
        )}
        {canScanBarcode && isInitialized && (
          <BarcodeScanner
            isOn={showBarcodeModal}
            updateBarcodeValue={updateBarcodeValue}
            closeBarcodeModal={closeBarcodeModal}
          />
        )}
      </div>
      <Annotate
        showModal={showAnnotateModal}
        reportName={REPORT_NAME}
        onClose={closeAnnotateModal}
        contentRef={mainScreenContentRef}
      />
      <UnitInfoModal
        isOpen={isOpenUnitInfoModal}
        kpiDefinition={kpiDefinition}
        reportConsiderations={reportConsiderations}
        closeHandler={() => setIsOpenUnitInfoModal(false)}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isInitialized: state.dashboard.isStateInitialized,
  isDetectedLocation: state.dashboard.isDetectedLocation,
  selectedFilterOptions: state.dashboard.selectedFilterOptions,
  sellingUnitsData: state.dashboard.sellingUnitsData,
  shippingUnitsData: state.dashboard.shippingUnitsData,
  vendorUnitsData: state.dashboard.vendorUnitsData,
  totalWarehouseData: state.dashboard.totalWarehouseData,
  totalStoreData: state.dashboard.totalStoreData,
  itemType: state.dashboard.itemType,
  refreshDataKey: state.dashboard.refreshDataKey,
  isUnitsDataLoading: state.dashboard.isUnitsDataLoading,
  productDescription: state.dashboard.productDescription,
  isTrackedViewReport: state.monitor.isTrackedViewReport,
  selectedUnitTab: state.dashboard.selectedUnitTab,
  selectedHistoryTab: state.orderHistory.selectedHistoryTab,
  dcResearchData: state.dcResearch.dcResearchData,
  retailAdjustmentsData: state.retailAdjustments.retailAdjustmentsData,
  isDcResearchCardsLoading: state.dcResearch.isDcResearchCardsLoading,
  isLoadingRetailAdjustmentsData:
    state.retailAdjustments.isLoadingRetailAdjustmentsData,
  isLoadingDcStoreData: state.orderHistory.isLoadingDcStoreData,
  isLoadingStoreOrderData: state.orderHistory.isLoadingStoreOrderData,
  isLoadingBuyerCommentsData: state.orderHistory.isLoadingBuyerCommentsData,
  isLoadingOrderHistoryData: state.dsdOrderHistory.isLoadingOrderHistoryData,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  initDashboardState: () => dispatch(initDashboardState()),
  fetchUnitsData: (filters: IFilterOptions, isRetailOps?: boolean) =>
    dispatch(fetchUnitsData(filters, isRetailOps)),
  setSelectedUnitTab: (value: UnitTab) =>
    dispatch(setSelectedUnitTabAction(value)),
  resetOrderHistory: () => dispatch(resetOrderHistory()),
  fetchDcStoreData: (filters: IFilterOptions) =>
    dispatch(fetchDcStoreData(filters)),
  fetchStoreOrderData: (filters: IFilterOptions) =>
    dispatch(fetchStoreOrderData(filters)),
  fetchBuyerCommentsData: (filters: IFilterOptions) =>
    dispatch(fetchBuyerCommentsData(filters)),
  fetchDcStoreTotal: (filters: IFilterOptions) =>
    dispatch(fetchDcStoreTotal(filters)),
  fetchStoreOrderTotal: (filters: IFilterOptions) =>
    dispatch(fetchStoreOrderTotal(filters)),
  fetchOrderHistoryDsdData: (filters: IFilterOptions) =>
    dispatch(fetchOrderHistoryDsdData(filters)),
  fetchOrderHistoryDsdTotalQuantity: (filters: IFilterOptions) =>
    dispatch(fetchOrderHistoryDsdTotalQuantity(filters)),
  fetchDCResearchCards: (filters: IFilterOptions, fields: string[]) =>
    dispatch(fetchDcResearchData(filters, fields)),
  fetchRetailAdjustmentsData: (filters: IFilterOptions) =>
    dispatch(fetchRetailAdjustmentsData(filters)),
  monitorViewReport: (data: IMonitorViewReport) =>
    dispatch(monitorViewReport(data)),
  monitorBarcodeScanUsage: (data: IBarcodeUsage) =>
    dispatch(monitorBarcodeScanUsage(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ReportLayout);
